// src/hooks/useEmployees.ts - Version multi-tenant
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  orderBy,
  where,
  deleteField,
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { COLLECTIONS } from '../config/collections';
import { Employee } from '../types/entities';
import { useAuth } from '../contexts/AuthContext';
import { usePermissions } from './usePermissions';
import { handleFirestoreError } from '../utils/firestoreErrorHandler';
import { sanitizeEmployeeActivityReportConfig } from '../utils/activityReports';
import {
  normalizeEmployeeSiteAssignment,
  parseAssignedSiteIds,
} from '../utils/employeeSites';
import { AppError } from '../utils/i18n/AppError';

// ===== HOOKS POUR LES EMPLOYÉS =====
export const useEmployees = () => {
  const { currentBusiness, isSuperAdmin, isCSM } = useAuth();
  const permissions = usePermissions();
  const businessId = currentBusiness?.id;

  return useQuery({
    queryKey: [ 'employees', businessId],
    queryFn: async () => {
      if (!businessId) {
        console.warn('Aucun businessId disponible');
        return [];
      }

      const q = query(
        collection(db, COLLECTIONS.employees),
        where('businessId', '==', businessId),
        where('isActive', '==', true),  // ✅ Filtrer par défaut les employees actifs
        orderBy('name')
      );
      const snapshot = await getDocs(q).catch(error => {
        handleFirestoreError(error, 'useEmployees - Main query');
        throw error;
      });
      
      const all = snapshot.docs.map(docSnap => {
        const data = docSnap.data();
        const employee: Employee = {
          id: docSnap.id,
          businessId: data.businessId,
          name: data.name,
          role: data.role || 'employee',
          roleProfileId: data.roleProfileId || undefined,
          userId: data.userId,
          email: data.email,
          phone: data.phone,
          isActive: data.isActive ?? true,
          defaultSiteId: typeof data.defaultSiteId === 'string' ? data.defaultSiteId : undefined,
          assignedSiteIds: parseAssignedSiteIds(data.assignedSiteIds),
          baseSalary: data.baseSalary,
          paymentInfo: typeof data.paymentInfo === 'string' ? data.paymentInfo : undefined,
          plannedVariableAmount: data.plannedVariableAmount,
          // undefined / absent → false (employés sans compte app). Seul true explicite = SA/CSM
          isSuperAdmin: data.isSuperAdmin === true,
          isCSM: data.isCSM === true,
          activityReportConfig: data.activityReportConfig || undefined,
        };
        return employee;
      });
      // Côté client (institut) : ne pas afficher Super Admin ni CSM dans la liste des employés
      return all.filter(
        (emp) => emp.isSuperAdmin !== true && emp.isCSM !== true
      ) as Employee[];
    },
    // Activer si l'utilisateur peut voir les employés, POS, opportunités, ou qu'il utilise les rapports d'activité
    enabled:
      !!businessId &&
      (
        !!(permissions?.canViewEmployees ?? false) ||
        !!(permissions?.canCreatePrestations ?? false) ||
        !!(permissions?.canCreateProductSales ?? false) ||
        // Rapports d'activité : on a besoin de la fiche employé (config) même si l'utilisateur ne voit pas la liste complète
        !!(permissions?.canViewActivityReports ?? false) ||
        !!(permissions?.canCreateActivityReports ?? false) ||
        (!!(permissions?.canViewSalesPipeline ?? false) && (isSuperAdmin || isCSM))
      ),
  });
};

/**
 * Récupère les noms des employés par ID (getDoc par doc).
 * Utilisé quand l'utilisateur a le droit de voir des rapports (ex. rapports d'activité) mais pas
 * canViewEmployees : les règles Firestore permettent la lecture d'un doc employee si canAccess(businessId).
 */
export const useEmployeeNamesByIds = (employeeIds: string[]) => {
  const { currentBusiness } = useAuth();
  const businessId = currentBusiness?.id;
  const uniqueIds = Array.from(new Set(employeeIds)).filter(Boolean);

  return useQuery({
    queryKey: ['employeeNames', businessId, uniqueIds.sort().join(',')],
    queryFn: async () => {
      if (!businessId || uniqueIds.length === 0) return {} as Record<string, string>;
      const results = await Promise.all(
        uniqueIds.map((id) => getDoc(doc(db, COLLECTIONS.employees, id)))
      );
      const map: Record<string, string> = {};
      results.forEach((snap, i) => {
        if (snap.exists() && snap.data()?.name) {
          map[uniqueIds[i]] = snap.data().name as string;
        }
      });
      return map;
    },
    enabled: !!businessId && uniqueIds.length > 0,
  });
};

export const useAddEmployee = () => {
  const queryClient = useQueryClient();
  const { currentBusiness, isSuperAdmin, isCSM } = useAuth();
const businessId = currentBusiness?.id;
  
  return useMutation({
    mutationFn: async (employee: Omit<Employee, 'id' | 'businessId'>) => {
      if (!businessId) {
        throw new Error('Institut non défini');
      }
      if (!isSuperAdmin && !isCSM) {
        throw new AppError('superAdminOrCsmOnly');
      }
      
      const siteNorm = normalizeEmployeeSiteAssignment({
        assignedSiteIds: employee.assignedSiteIds,
        defaultSiteId: employee.defaultSiteId,
      });

      const cleanedEmployee: any = {
        ...employee,
        businessId,
        email: employee.email || undefined,
        phone: employee.phone || undefined,
        // Gérer baseSalary : si valeur > 0, on garde, sinon on supprime
        baseSalary: (employee.baseSalary && employee.baseSalary > 0) ? employee.baseSalary : undefined,
        paymentInfo:
          typeof employee.paymentInfo === 'string' && employee.paymentInfo.trim()
            ? employee.paymentInfo.trim()
            : undefined,
        assignedSiteIds: siteNorm.assignedSiteIds,
        defaultSiteId: siteNorm.defaultSiteId,
        // Employés créés côté client = pas d'accès app, jamais SA ni CSM
        isSuperAdmin: false,
        isCSM: false,
      };
      
      // ✅ Supprimer roleProfileId et userId - Seul le Super Admin peut les assigner
      delete cleanedEmployee.roleProfileId;
      delete cleanedEmployee.userId;
      
      // Gérer activityReportConfig : ne l'inclure que si validationMode est défini
      if (cleanedEmployee.activityReportConfig) {
        const sanitized = sanitizeEmployeeActivityReportConfig(cleanedEmployee.activityReportConfig);
        if (!sanitized) {
          delete cleanedEmployee.activityReportConfig;
        } else {
          cleanedEmployee.activityReportConfig = sanitized;
        }
      }
      
      // Supprimer les champs undefined
      Object.keys(cleanedEmployee).forEach(key => {
        if (cleanedEmployee[key] === undefined) {
          delete cleanedEmployee[key];
        }
      });
      
      const docRef = await addDoc(collection(db, COLLECTIONS.employees), cleanedEmployee);
      return docRef.id;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [ 'employees', businessId] });
    },
  });
};

export const useUpdateEmployee = () => {
  const queryClient = useQueryClient();
  const { currentBusiness } = useAuth();
const businessId = currentBusiness?.id;
  
  return useMutation({
    mutationFn: async ({ id, ...data }: Partial<Employee> & { id: string }) => {
      const cleanedData = { ...data };
      
      // Supprimer businessId des mises à jour (sécurité)
      delete cleanedData.businessId;
      
      // ✅ Supprimer roleProfileId et userId - Seul le Super Admin peut les modifier
      delete cleanedData.roleProfileId;
      delete cleanedData.userId;
      // Ne jamais modifier isSuperAdmin/isCSM côté client (réservé au backend)
      delete cleanedData.isSuperAdmin;
      delete cleanedData.isCSM;
      
      // Gérer les champs optionnels
      if ('email' in cleanedData && !cleanedData.email) {
        cleanedData.email = undefined;
      }
      if ('phone' in cleanedData && !cleanedData.phone) {
        cleanedData.phone = undefined;
      }
      // Gérer baseSalary : si 0 ou vide, on le laisse à undefined pour le supprimer
      // Sinon on garde la valeur
      if ('baseSalary' in cleanedData) {
        if (!cleanedData.baseSalary || cleanedData.baseSalary === 0) {
          cleanedData.baseSalary = undefined;
        }
      }
      let paymentInfoToDelete = false;
      if ('paymentInfo' in cleanedData) {
        const raw = cleanedData.paymentInfo;
        if (typeof raw !== 'string' || !raw.trim()) {
          paymentInfoToDelete = true;
          delete cleanedData.paymentInfo;
        } else {
          cleanedData.paymentInfo = raw.trim();
        }
      }
      if ('plannedVariableAmount' in cleanedData) {
        if (
          cleanedData.plannedVariableAmount == null ||
          cleanedData.plannedVariableAmount === 0 ||
          Number(cleanedData.plannedVariableAmount) <= 0
        ) {
          cleanedData.plannedVariableAmount = undefined;
        }
      }

      let assignedSiteIdsToDelete = false;
      if ('assignedSiteIds' in cleanedData || 'defaultSiteId' in cleanedData) {
        const siteNorm = normalizeEmployeeSiteAssignment({
          assignedSiteIds: cleanedData.assignedSiteIds,
          defaultSiteId: cleanedData.defaultSiteId,
        });
        if (!siteNorm.assignedSiteIds) {
          assignedSiteIdsToDelete = true;
          delete cleanedData.assignedSiteIds;
        } else {
          cleanedData.assignedSiteIds = siteNorm.assignedSiteIds;
        }
        if (siteNorm.defaultSiteId) {
          cleanedData.defaultSiteId = siteNorm.defaultSiteId;
        } else if ('defaultSiteId' in cleanedData) {
          cleanedData.defaultSiteId = undefined;
        }
      }
      
      // Gérer activityReportConfig : si validationMode est vide, supprimer la config
      let activityReportConfigToDelete = false;
      if ('activityReportConfig' in cleanedData) {
        const sanitized = sanitizeEmployeeActivityReportConfig(cleanedData.activityReportConfig);
        if (!sanitized) {
          activityReportConfigToDelete = true;
          delete cleanedData.activityReportConfig;
        } else {
          cleanedData.activityReportConfig = sanitized;
        }
      }
      
      // Supprimer les champs undefined
      Object.keys(cleanedData).forEach(key => {
        if (cleanedData[key as keyof typeof cleanedData] === undefined) {
          delete cleanedData[key as keyof typeof cleanedData];
        }
      });
      
      // Préparer l'objet de mise à jour avec deleteField si nécessaire
      const updateData: any = { ...cleanedData };
      if (activityReportConfigToDelete) {
        updateData.activityReportConfig = deleteField();
      }
      if (paymentInfoToDelete) {
        updateData.paymentInfo = deleteField();
      }
      if (assignedSiteIdsToDelete) {
        updateData.assignedSiteIds = deleteField();
      }
      
      const docRef = doc(db, COLLECTIONS.employees, id);
      await updateDoc(docRef, updateData);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [ 'employees', businessId] });
    },
  });
};

export const useDeleteEmployee = () => {
  const queryClient = useQueryClient();
  const { currentBusiness } = useAuth();
const businessId = currentBusiness?.id;
  
  return useMutation({
    mutationFn: async (id: string) => {
      await deleteDoc(doc(db, COLLECTIONS.employees, id));
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [ 'employees', businessId] });
    },
  });
};
/** Fiche employé de l’utilisateur connecté pour le business courant. */
export const useCurrentEmployee = () => {
  const { currentBusiness, userProfile } = useAuth();
  const businessId = currentBusiness?.id;
  const employeeId =
    businessId && userProfile?.employeeIds?.[businessId]
      ? userProfile.employeeIds[businessId]
      : undefined;

  return useQuery({
    queryKey: ['employees', 'current', businessId, employeeId],
    queryFn: async (): Promise<Employee | null> => {
      if (!businessId || !employeeId) return null;
      const snap = await getDoc(doc(db, COLLECTIONS.employees, employeeId));
      if (!snap.exists()) return null;
      const data = snap.data();
      if (data.businessId !== businessId) return null;
      return {
        id: snap.id,
        businessId: data.businessId,
        name: data.name,
        role: data.role || 'employee',
        roleProfileId: data.roleProfileId || undefined,
        userId: data.userId,
        email: data.email,
        phone: data.phone,
        isActive: data.isActive ?? true,
        defaultSiteId: typeof data.defaultSiteId === 'string' ? data.defaultSiteId : undefined,
        assignedSiteIds: parseAssignedSiteIds(data.assignedSiteIds),
        baseSalary: data.baseSalary,
        paymentInfo: typeof data.paymentInfo === 'string' ? data.paymentInfo : undefined,
        plannedVariableAmount: data.plannedVariableAmount,
        isSuperAdmin: data.isSuperAdmin === true,
        isCSM: data.isCSM === true,
        activityReportConfig: data.activityReportConfig || undefined,
      } as Employee;
    },
    enabled: !!businessId && !!employeeId,
  });
};

/** Enregistre le lieu de travail par défaut de l’employé connecté (business courant). */
export const useSetMyDefaultSite = () => {
  const queryClient = useQueryClient();
  const { currentBusiness, userProfile } = useAuth();
  const businessId = currentBusiness?.id;
  const employeeId =
    businessId && userProfile?.employeeIds?.[businessId]
      ? userProfile.employeeIds[businessId]
      : undefined;

  return useMutation({
    mutationFn: async (siteId: string) => {
      if (!businessId || !employeeId) {
        throw new Error('Aucune fiche employé associée à cet établissement.');
      }
      if (!siteId.trim()) {
        throw new Error('Veuillez sélectionner un lieu.');
      }
      const siteRef = doc(db, COLLECTIONS.sites, siteId);
      const siteSnap = await getDoc(siteRef);
      if (!siteSnap.exists()) {
        throw new Error('Lieu introuvable.');
      }
      const siteData = siteSnap.data();
      if (siteData.businessId !== businessId || siteData.isActive === false) {
        throw new Error('Ce lieu n’est pas disponible pour cet établissement.');
      }
      const empSnap = await getDoc(doc(db, COLLECTIONS.employees, employeeId));
      if (!empSnap.exists()) {
        throw new Error('Fiche employé introuvable.');
      }
      const empData = empSnap.data();
      const assigned = parseAssignedSiteIds(empData.assignedSiteIds);
      if (assigned && assigned.length > 0 && !assigned.includes(siteId)) {
        throw new Error('Ce lieu ne fait pas partie de vos lieux autorisés.');
      }
      await updateDoc(doc(db, COLLECTIONS.employees, employeeId), {
        defaultSiteId: siteId,
      });
      return siteId;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['employees', 'current', businessId, employeeId] });
      queryClient.invalidateQueries({ queryKey: ['employees', businessId] });
    },
  });
};
